import React, { useState } from 'react';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

// URLはアイコンリンク、テキストはそのまま表示
const CellContent = ({ value }) => {
  if (!value && value !== 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
  const str = Array.isArray(value) ? value.join('、') : String(value);
  if (str.startsWith('http')) return (
    <a href={str} target="_blank" rel="noreferrer"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#f5f3ff', color: '#6c63ff', fontSize: 11, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}
      title={str}>
      🔗 リンクを開く
    </a>
  );
  return <span style={{ fontSize: 12, color: '#334155', wordBreak: 'break-word', lineHeight: 1.5 }}>{str}</span>;
};

// フィールドキーに応じたアイコンリンク
const IconLink = ({ fieldKey, value }) => {
  if (!value) return <span style={{ color: '#cbd5e1' }}>—</span>;
  if (fieldKey === 'x_url') return (
    <a href={value} target="_blank" rel="noreferrer" title={value}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 900, textDecoration: 'none' }}>
      𝕏
    </a>
  );
  if (fieldKey === 'vrchat_profile') return (
    <a href={value} target="_blank" rel="noreferrer" title={value}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, background: '#6c63ff', color: '#fff', fontSize: 9, fontWeight: 800, textDecoration: 'none', letterSpacing: -0.5 }}>
      VRC
    </a>
  );
  if (fieldKey === 'discord') return (
    <span style={{ fontSize: 12, color: '#5865f2', fontWeight: 600 }}>{value}</span>
  );
  return <CellContent value={value} />;
};

const csvEscape = (value) => {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const fmtTimestampForFilename = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const ApplicationsView = ({ applications, formDef, event, formToken }) => {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [csvScope, setCsvScope] = useState('all');

  const handleCopyUrl = () => {
    const url = `${window.location.origin}/apply/${formToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const appList = Object.entries(applications || {})
    .map(([id, app]) => ({ id, ...app }))
    .sort((a, b) => (b.submitted_at || 0) - (a.submitted_at || 0));

  const filtered = search.trim()
    ? appList.filter(a => a.vrchat_name?.toLowerCase().includes(search.toLowerCase()))
    : appList;

  const formFields  = formDef?.fields || [];
  const customFields = formFields.filter(f => f.type === 'custom');

  const total = appList.length;

  const winnerIds = new Set((event?.results?.pairs || []).map(p => p.guest_id));
  const hasResults = winnerIds.size > 0;

  const handleDownloadCsv = () => {
    const target = csvScope === 'winners' ? filtered.filter(a => winnerIds.has(a.guest_id)) : filtered;
    const headers = ['VRChat名', 'VRChatプロフィール', 'X', 'Discord', ...customFields.map(cf => cf.label), '応募日時'];
    const rows = target.map(app => [
      app.vrchat_name || '',
      app.vrchat_profile || '',
      app.x_url || '',
      app.discord || '',
      ...customFields.map(cf => {
        const v = app.answers?.[cf.id];
        return Array.isArray(v) ? v.join('、') : (v ?? '');
      }),
      app.submitted_at ? new Date(app.submitted_at).toLocaleString('ja-JP') : '',
    ]);
    const csv = [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scopeSuffix = csvScope === 'winners' ? '_当選者のみ' : '';
    a.download = `応募リスト(${event?.event_name || ''})${scopeSuffix}_${fmtTimestampForFilename(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '24px', background: '#f8fafc', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>

      {/* サマリーヘッダー */}
      <div style={{
        flexShrink: 0,
        background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.04)', border: '1px solid #e8edf5',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1.5, textTransform: 'uppercase' }}>応募状況</p>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#0f172a' }}>{event?.event_name}</h2>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {event?.status === 'matching' && formToken && (
              <button
                onClick={handleCopyUrl}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${copied ? '#10b981' : '#6c63ff'}`, background: copied ? '#ecfdf5' : '#f5f3ff', color: copied ? '#059669' : '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
              >
                {copied ? '✓ コピー完了！' : '🔗 応募URLをコピー'}
              </button>
            )}
            {total > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {hasResults && (
                  <select
                    value={csvScope}
                    onChange={e => setCsvScope(e.target.value)}
                    style={{ padding: '8px 10px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#475569', background: '#fff', cursor: 'pointer', outline: 'none' }}
                  >
                    <option value="all">全員</option>
                    <option value="winners">当選者のみ</option>
                  </select>
                )}
                <button
                  onClick={handleDownloadCsv}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #6c63ff', background: '#f5f3ff', color: '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
                >
                  📊 CSVダウンロード
                </button>
              </div>
            )}
            <div style={{ textAlign: 'center', padding: '8px 20px', background: '#f5f3ff', borderRadius: 12, border: '1px solid #c4b5fd' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#6c63ff', lineHeight: 1 }}>{total}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>応募者数</div>
            </div>
            {event?.capacity && (
              <div style={{ textAlign: 'center', padding: '8px 20px', background: '#ecfdf5', borderRadius: 12, border: '1px solid #6ee7b7' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#10b981', lineHeight: 1 }}>{event.capacity}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>定員</div>
              </div>
            )}
          </div>
        </div>

        {/* 検索 */}
        <div style={{ marginTop: 16 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="VRChat名で検索..."
            style={{
              width: '100%', maxWidth: 280, padding: '7px 12px', borderRadius: 8,
              border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* テーブル */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#94a3b8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <p style={{ margin: 0, fontWeight: 600 }}>{total === 0 ? 'まだ応募がありません' : '該当する応募者が見つかりません'}</p>
        </div>
      ) : (
        <div style={{
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 16, border: '1px solid #e8edf5',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
        }}>
          <div style={{ flex: 1, minHeight: 0, overflowX: 'scroll', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e8edf5' }}>
                  <Th>VRChat名</Th>
                  <Th center>VRC</Th>
                  <Th center>𝕏</Th>
                  <Th>Discord</Th>
                  {customFields.map(cf => <Th key={cf.id}>{cf.label}</Th>)}
                  <Th>応募日時</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((app, idx) => (
                  <tr key={app.id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbff' }}>
                    <Td>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{app.vrchat_name || '—'}</span>
                    </Td>
                    <Td center><IconLink fieldKey="vrchat_profile" value={app.vrchat_profile} /></Td>
                    <Td center><IconLink fieldKey="x_url"          value={app.x_url} /></Td>
                    <Td><IconLink fieldKey="discord"               value={app.discord} /></Td>
                    {customFields.map(cf => (
                      <Td key={cf.id}><CellContent value={app.answers?.[cf.id]} /></Td>
                    ))}
                    <Td>
                      <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(app.submitted_at)}</span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* フッター */}
          <div style={{ flexShrink: 0, padding: '10px 20px', borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>
            {filtered.length} 件表示 / 合計 {total} 件
          </div>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, center }) => (
  <th style={{
    padding: '10px 14px', textAlign: center ? 'center' : 'left', fontSize: 11, fontWeight: 700,
    color: '#64748b', letterSpacing: '0.5px', whiteSpace: 'nowrap',
  }}>{children}</th>
);

const Td = ({ children, center }) => (
  <td style={{ padding: '10px 14px', verticalAlign: 'middle', maxWidth: 220, textAlign: center ? 'center' : 'left' }}>{children}</td>
);

export default ApplicationsView;
